import styled, { css } from 'styled-components';
import { CurrencyCode } from '@/helpers/currency';
import { hexToRGB, typography } from '@/styles/helpers';
import type { ScSelectBlockProps } from './types';

interface ScInputProps {
  error: boolean;
  icon: boolean;
  value?: unknown;
  field?: string;
  approved?: boolean;
  prefix?: string;
}

const prefixPadding: Record<string, number> = {
  [CurrencyCode.USDT]: 66,
};

export const ScInput = styled.input<ScInputProps>`
  ${typography['body1regular']}
  color: ${({ theme, value }) => (value ? theme.white : theme.color2)};
  height: 40px;
  width: 100%;
  border: 2px solid transparent;
  border-radius: 24px;
  padding: ${({ icon, prefix }) =>
    prefix
      ? `8px 16px 8px ${prefixPadding[prefix] || 56}px`
      : icon
        ? '8px 16px 8px 36px'
        : '8px 16px'};
  background-color: ${({ theme }) => theme.color3};
  transition:
    padding 0.2s,
    border-color 0.3s;
  appearance: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &::placeholder {
    color: ${({ theme }) => theme.color2};
    transition: color 0.3s;
  }

  &:focus {
    border-color: ${({ theme }) => theme.color20};
    color: ${({ theme }) => theme.white};

    &::placeholder {
      color: ${({ theme }) => theme.white};
    }

    @media (hover: none) {
      &[name='search']::placeholder {
        color: ${({ theme }) => theme.color2};
      }
    }
  }

  &:disabled {
    opacity: 0.4;
    color: ${({ theme }) => theme.color2};
    border-color: transparent;
  }

  ${({ error, theme }) =>
    error &&
    css`
      border-color: ${theme.error};
    `}

  @media (hover: hover) {
    &:not(:disabled):hover {
      border-color: ${({ theme, value, error, approved }) =>
        !!value && !error && !approved && theme.color20};
    }

    &::placeholder {
      color: ${({ theme }) => theme.white};
    }
  }

  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    display: none;
  }
`;

export const ScSelectBlock = styled.div<ScSelectBlockProps>`
  position: relative;
  z-index: ${({ active }) => (active ? 1 : 0)};

  & > .select-button {
    ${typography['body1regular']}
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    color: ${({ theme, active, selected, isFilter }) =>
      selected && isFilter
        ? theme.color3
        : active
          ? theme.white
          : theme.color2};
    height: 40px;
    width: 100%;
    border: ${({ theme, active, error }) =>
      `2px solid ${error ? theme.redColor : active ? theme.color20 : 'transparent'}`};
    border-radius: 24px;
    padding: 0 36px 0 16px;
    background-color: ${({ theme, secondary, selected, isFilter }) =>
      selected && isFilter
        ? theme.white
        : secondary
          ? theme.color1
          : theme.color3};
    transition: border-color 0.2s;
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

    & > .value {
      display: flex;
      gap: 8px;
      width: 100%;

      & .name {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      & .with-icon {
        position: relative;

        img {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          object-fit: contain;
          margin-right: 8px;
        }
      }

      .placeholder {
        color: ${({ theme, disabled, active }) =>
          active
            ? theme.white
            : disabled
              ? theme.grayMegaLightColor
              : theme.blackLightColor};
      }
    }

    & > .arrow {
      position: absolute;
      right: 16px;
      top: 50%;
      transition: transform 0.5s;
      transform: ${({ active }) =>
        `translateY(-50%) ${active ? 'rotate(0deg)' : 'rotate(180deg)'}`};

      i {
        display: block;
        transform: rotate(-90deg);
        color: ${({ theme }) => theme.grayColor};
      }
    }

    & > .cross {
      position: absolute;
      right: 4px;
      width: 40px;
      height: 40px;
    }

    ${({ disabled, selected, isFilter }) =>
      disabled
        ? css`
            opacity: 0.4;
            color: ${({ theme }) => theme.color2};
          `
        : selected &&
          css`
            color: ${({ theme }) => (isFilter ? theme.color3 : theme.white)};
          `}

    ${({ selected, isFilter, disabled }) =>
      !(disabled || (selected && isFilter)) &&
      css`
        @media (hover: hover) {
          &:hover {
            & > .value,
            & > .value .placeholder {
              color: ${({ theme }) => theme.white};
            }

            & > .arrow i {
              color: ${({ theme }) => theme.white};
            }
          }
        }
      `}

      @media (hover: hover) {
      &:hover {
        & > .cross i {
          color: ${({ theme }) => theme.color3};
        }
      }
    }
  }
`;

export interface ScOptionsProps {
  dropdownUp?: boolean;
  minOptionsWidth?: number;
  optionsPosition?: string;
  search?: boolean;
}

export const ScOptions = styled.div<ScOptionsProps>`
  position: absolute;
  min-width: ${({ minOptionsWidth }) =>
    minOptionsWidth ? `${minOptionsWidth}px` : 'auto'};

  ${({ dropdownUp }) =>
    dropdownUp
      ? css`
          bottom: 100%;
          margin-bottom: 8px;
        `
      : css`
          top: 100%;
          margin-top: 8px;
        `}
  left: 0;
  right: 0;
  inset: ${({ optionsPosition }) => optionsPosition ?? 'auto 0'};
  padding: 0px 16px;
  background: ${({ theme }) => theme.color3};
  box-shadow: ${({ theme }) =>
    `0px 2px 10px ${hexToRGB(theme.blackColor, 0.3)}`};
  border: ${({ theme }) => `2px solid ${theme.color20}`};
  border-radius: 24px;
  overflow: hidden;

  & > .content {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  ul {
    display: block;
    list-style: none;

    & > li {
      padding: 8px 0;
      cursor: pointer;
      background-color: transparent;
      color: ${({ theme }) => theme.blackLightColor};
      transition: color 0.2s;
      line-height: 24px;
      display: flex;
      position: relative;

      &.active .content {
        width: calc(100% - 32px);
      }

      .content {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        line-height: 24px;
      }

      svg {
        width: 24px;
        position: absolute;
        right: 2px;
      }

      &.not-found {
        text-align: center;
        font-size: 12px;
      }

      &.with-icon {
        position: relative;

        & > .content {
          line-height: 1;
        }

        img {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          object-fit: contain;
          margin-right: 8px;
        }
      }

      &.active,
      &:hover {
        color: ${({ theme }) => theme.white};
      }

      &:first-child {
        padding-top: ${({ search }) => (search ? 0 : '16px')};
      }

      &:last-child {
        padding-bottom: 16px;
      }
    }
  }
`;

export const ScSelectSearch = styled.div`
  padding: 12px 0 8px;

  & input {
    width: 100%;
    height: 20px;
    background: transparent;
    border: transparent;
    color: ${({ theme }) => theme.white};
    -webkit-text-fill-color: initial;
    appearance: none;
    outline: none;
    font-size: 16px;
    line-height: 20px;
    font-weight: 400px;

    &::placeholder {
      color: ${({ theme }) => theme.color20};
    }
  }

  & .divider {
    height: 2px;
    background-color: ${({ theme }) => theme.whiteColor};
    margin: 4px auto 0;
    border-radius: 1px;
  }
`;
